import { ChangeDetectorRef, Component, ElementRef, Input, OnChanges, OnInit, SimpleChanges, ViewChild } from '@angular/core';

import { CommonModule } from '@angular/common';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectChange, MatSelectModule } from '@angular/material/select';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { DocumentsService } from '../../services/documents/documents.service';
import { DocumentRecord } from '../../services/documents/document.interface';
import { StandardsDialogComponent } from '../../pages/standards-group/standards-dialog/standards-dialog.component';

// Generic replacement for the mat-select + inline-viewer pattern duplicated
// across HR/Standards/Operations pages (hardcoded arrays of {name, path,
// isForm, isImage}). Documents now come from DocumentsService instead of a
// static array; isForm -> openInNewTab and isImage is derived from mimeType.
@Component({
  selector: 'app-document-picker',
  standalone: true,
  imports: [
    CommonModule,
    MatFormFieldModule,
    MatSelectModule,
    MatDialogModule,
    MatProgressSpinnerModule,
  ],
  templateUrl: './document-picker.component.html',
  styleUrl: './document-picker.component.css',
})
export class DocumentPickerComponent implements OnInit, OnChanges {
  @Input({ required: true }) category!: string;
  @Input() subcategory = '';
  @Input() label = 'Select a document';

  @ViewChild('pdfViewer') pdfViewer?: ElementRef<HTMLElement>;
  @ViewChild('imageViewer') imageViewer?: ElementRef<HTMLElement>;

  docs: DocumentRecord[] = [];
  loading = false;
  resolving = false;
  error: string | null = null;
  selectedOption: SafeResourceUrl | null = null;
  selectedImage: string | null = null;

  constructor(
    private documentsService: DocumentsService,
    private sanitizer: DomSanitizer,
    private dialog: MatDialog,
    private cdRef: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.loadDocuments();
  }

  ngOnChanges(changes: SimpleChanges): void {
    const categoryChanged = changes['category'] && !changes['category'].firstChange;
    const subcategoryChanged = changes['subcategory'] && !changes['subcategory'].firstChange;
    if (categoryChanged || subcategoryChanged) {
      this.resetViewer();
      this.loadDocuments();
    }
  }

  private loadDocuments(): void {
    this.loading = true;
    this.documentsService.getDocuments(this.category, this.subcategory || undefined).subscribe({
      next: (docs) => {
        this.docs = docs;
        this.loading = false;
      },
      error: (err) => {
        console.error('Failed to load documents:', err);
        this.loading = false;
      },
    });
  }

  private resetViewer(): void {
    this.selectedOption = null;
    this.selectedImage = null;
    this.error = null;
  }

  isImage(doc: DocumentRecord): boolean {
    return doc.mimeType.startsWith('image/');
  }

  onOptionChange(event: MatSelectChange): void {
    const doc = this.docs.find((d) => d._id === event.value);
    this.error = null;
    if (!doc) return;

    if (doc.openInNewTab) {
      const dialogRef = this.dialog.open(StandardsDialogComponent, {
        width: '400px',
        data: { name: doc.title, path: '' },
      });
      dialogRef.afterClosed().subscribe((result) => {
        if (result === 'open') {
          this.documentsService.getSignedUrl(doc._id).subscribe({
            next: ({ url }) => window.open(url, '_blank'),
            error: (err) => {
              console.error('Failed to get signed URL:', err);
              this.error = 'Failed to open document.';
            },
          });
        }
      });
      this.resetViewer();
      return;
    }

    this.resetViewer();
    this.resolving = true;
    this.documentsService.getSignedUrl(doc._id).subscribe({
      next: ({ url }) => {
        this.resolving = false;
        if (this.isImage(doc)) {
          this.selectedImage = url;
        } else {
          this.selectedOption = this.sanitizer.bypassSecurityTrustResourceUrl(`${url}#toolbar=0&navpanes=0`);
        }
        this.cdRef.detectChanges();
        setTimeout(() => this.scrollToViewer(), 200);
      },
      error: (err) => {
        console.error('Failed to get signed URL:', err);
        this.resolving = false;
        this.error = 'Failed to load document.';
      },
    });
  }

  private scrollToViewer(): void {
    const element = this.pdfViewer?.nativeElement ?? this.imageViewer?.nativeElement;
    if (element) {
      const yOffset = -75;
      const yPosition = element.getBoundingClientRect().top + window.scrollY + yOffset;
      window.scrollTo({ top: yPosition, behavior: 'smooth' });
    }
  }
}
